import { Injectable } from '@angular/core';
import { Observable, Observer, TimeoutError } from 'rxjs';
import { Subject } from 'rxjs';
import { fromEvent, merge } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HTTP } from '@ionic-native/http/ngx';
import { Network } from '@ionic-native/network/ngx';

import { map, timeout } from 'rxjs/operators';

import { AppConstants} from '@app/app.constants';
import { platformService } from '@app/modules/common/platform';

class onlineService {
    private _isOnline = true; 

    private _onOnlineChanged = new Subject <any> ();
    public OnOnlineChanged = this._onOnlineChanged.asObservable();
    
    get isOnline(){
        return this._isOnline;
    }
    
    set isOnline(value){
        if (value != this._isOnline){
            this._isOnline = value;
            this._onOnlineChanged.next(this._isOnline);
        }
    }

    private _connect_subscription = null;
    private _disconn_subscription = null;
    private _dom_online_subscription = null;
    private _dom_offline_subscription = null;
    private _resync_timer: ReturnType<typeof setTimeout> | null = null;

    constructor(private http: httpService, private platform: platformService, private network: Network) {
        if (this.platform.is('cordova')){
            this.isOnline = this.network.type !== 'none';

            // Aplica el estado REAL del plugin (network.type es una consulta
            // nativa en vivo, siempre correcta). Se invoca diferido via
            // _scheduleResync: al reconectar, network.type puede tardar un
            // instante en reflejar el nuevo transporte.
            const _applyResync = () => {
                this._resync_timer = null;
                const _online = this.network.type !== 'none';
                if (_online != this.isOnline){
                    console.info("[local connection - resync] network.type=" + this.network.type + " -> online=" + _online);
                }
                this.isOnline = _online;
            };

            // Programa un resync diferido, reemplazando cualquiera pendiente:
            // como maximo hay UN timer vivo y siempre esta rastreado, para que
            // _goOffline pueda cancelarlo.
            const _scheduleResync = () => {
                if (this._resync_timer){
                    clearTimeout(this._resync_timer);
                }
                this._resync_timer = setTimeout(_applyResync, 0);
            };

            // Offline autoritativo: CANCELA cualquier resync diferido pendiente
            // antes de marcar false. Sin esto, un resync encolado por un
            // onConnect previo podia dispararse DESPUES del offline y, leyendo
            // un network.type aun 'none'-menos (stale), re-activar isOnline en
            // mitad de una caida real -> comandas enviadas en vez de encoladas.
            const _goOffline = () => {
                if (this._resync_timer){
                    clearTimeout(this._resync_timer);
                    this._resync_timer = null;
                }
                if (this.isOnline){
                    console.warn("[local connection - offline]");
                }
                this.isOnline = false;
            };

            // NO nos fiamos solo de que onConnect/onDisconnect hayan disparado:
            // en un cambio de TIPO de red (p.ej. 5G -> WiFi al volver al local,
            // sin pasar por 'none') el plugin nativo A VECES NO emite onConnect,
            // e isOnline se quedaba pegado en false. Con isOnline=false las
            // comandas remotas se ENCOLAN (offline) y no llegan a cocina hasta
            // reiniciar la app — el constructor volvia a leer network.type y por
            // eso "cerrar y abrir" lo arreglaba.
            this._disconn_subscription = this.network.onDisconnect().subscribe(() => _goOffline());
            this._connect_subscription = this.network.onConnect().subscribe(() => {
                console.info("[local connection - online]");
                _scheduleResync();
            });

            // Red de seguridad: el WebView dispara los eventos DOM online/offline
            // aunque el onConnect NATIVO se pierda en un cambio de tipo de red
            // (que es justo el fallo que dejaba las comandas sin salir).
            // Asimetrico a proposito: 'online' RECUPERA solo si network.type lo
            // confirma (via el resync rastreado); 'offline' es autoritativo.
            this._dom_online_subscription = fromEvent(window, 'online').subscribe(() => _scheduleResync());
            this._dom_offline_subscription = fromEvent(window, 'offline').subscribe(() => _goOffline());
        }
        else {
            this._doObserveOnline().subscribe(online => {
                if (online){
                    // Red local vuelve: cancelar backoff pendiente y sondear ya.
                    this.stopWaitOnline();
                    this._waitOnlineBackoffMs = 5000;
                    this.checkOnline({
                        next: () => {},
                        error: () => {},
                        complete: () => {}
                    }, true);
                }
                else {
                    this.stopWaitOnline();
                    if (this.isOnline){
                        console.warn("[local connection - offline]");
                    }
                    this.isOnline = false;
                }
            });

            this.isOnline = navigator.onLine;
        }
    }

    // https://stackoverflow.com/questions/46598777/check-internet-connection-in-web-using-angular-4    
    private _doObserveOnline() {
        return merge<boolean>(
            fromEvent(window, 'offline').pipe(map(() => false)),
            fromEvent(window, 'online').pipe(map(() => true)),
            new Observable((sub: Observer<boolean>) => {
                sub.next(navigator.onLine);
                sub.complete();
            })
        );
  }    
    
    private _retries = 0;
    private _waitOnlineTimer: ReturnType<typeof setTimeout> | null = null;
    private _waitOnlineBackoffMs = 5000;
    private static readonly _WAIT_ONLINE_MAX_MS = 30000;

    // Reintento de sonda al server SOLO cuando el browser cree que hay red
    // (navigator.onLine). Si no hay red local, esperamos al evento 'online'
    // del navegador — evita spamear online.php con ERR_NAME_NOT_RESOLVED.
    private waitOnline(){
        if (!navigator.onLine || this._waitOnlineTimer){
            return;
        }
        this._waitOnlineTimer = setTimeout(() => {
            this._waitOnlineTimer = null;
            let observable = new Observable((observer) => {
                this.checkOnline(observer);
            });
            // Predeclarar antes de subscribe: checkOnline puede emitir SINCRONO
            // (offline), disparando este callback antes de asignar _subscription
            // -> ReferenceError TDZ ('Cannot access _subscription before init').
            let _subscription = null;
            _subscription = observable.subscribe(
            data => {
                if (_subscription){ _subscription.unsubscribe(); }
                if (!data && navigator.onLine){
                    this._waitOnlineBackoffMs = Math.min(this._waitOnlineBackoffMs * 2, onlineService._WAIT_ONLINE_MAX_MS);
                    this.waitOnline();
                }
            });
        }, this._waitOnlineBackoffMs);
    }

    private stopWaitOnline(){
        if (this._waitOnlineTimer){
            clearTimeout(this._waitOnlineTimer);
            this._waitOnlineTimer = null;
        }
    }

    // Sin check al servidor si el browser ya sabe que no hay red — evita
    // spamear base/online.php. La reconexion la cubren waitOnline (server
    // caido con red local) y el evento 'online' del navegador (red local
    // caida y vuelta).
    public checkOnline(observer, forceProbe = false){
        if (!navigator.onLine){
            observer.next(false);
            observer.complete();
            this.stopWaitOnline();
            if (this.isOnline){
                console.warn("[local connection - offline]");
                this.isOnline = false;
            }
            return;
        }

        // Ya offline y hay sonda en curso — no lanzar otra (evita rafagas de
        // online.php cuando varios HTTP fallan a la vez). El evento 'online'
        // del navegador pasa forceProbe=true tras stopWaitOnline().
        if (!forceProbe && !this.isOnline && this._waitOnlineTimer){
            observer.next(false);
            observer.complete();
            return;
        }

        let _subscription = null;
        _subscription = this.http.check().subscribe(
        data => {
            this._retries = 0;
            this._waitOnlineBackoffMs = 5000;
            this.stopWaitOnline();

            observer.next(true);
            observer.complete();

            if (!this.isOnline){
                console.info("[server connection - online]");
            }

            this.isOnline = true;

        },
        error => {
            if (++this._retries >= 2){

                observer.next(false);
                observer.complete();

                if (this.isOnline){
                    console.warn("[online] exceeded retry count — now you are offline");
                    this.isOnline = false;
                }
                // navigator.onLine sigue true pero el server no responde:
                // hay que reintentar la sonda — sin esto isOnline queda false
                // para siempre y sync devuelve offlineObservable sin HTTP real.
                if (navigator.onLine){
                    this.waitOnline();
                }
            }
            else {
                console.warn("[online] server is not responding (retry count: " + this._retries + ")", error);
                setTimeout(() => {
                    this.checkOnline(observer);
                }, 250);
            }
        },
        () => {
            if (_subscription){ _subscription.unsubscribe(); }
        });
    }
};

@Injectable()
export class httpService {
    private online: onlineService = null;
    
    get IsOnline(){
        return this.online.isOnline;
    }

    get OnOnline(){
        return this.online.OnOnlineChanged;
    }

    constructor(private platform: platformService, private ionic: HTTP, private network: Network, private angular: HttpClient) { 
        this.online = new onlineService(this, this.platform, this.network); 
    }

    /********************************************/
    /* DEBUG TRACES                             */
    /********************************************/
    
    private _dbgTrace = false;
    private debuglog(...args: any[]){
        if (this._dbgTrace){
            console.info(...args);
        }
    }
    
    /********************************************/
    /* DEFAULT HEADER FOR HTTP REQUESTS         */
    /********************************************/
    
    public static get headers() {
        return {
            'Accept': 'text/html',
            'Content-Type': 'application/x-www-form-urlencoded'
        };
    };
    
    private _HttpError(observer, error){
        let observable = new Observable((_observer) => {
            this.online.checkOnline(_observer);
        });
        
        observer.error(error);

        // Predeclarar antes de subscribe: checkOnline emite SINCRONO cuando
        // esta offline (observer.next(false) inmediato), disparando este
        // callback antes de asignar _subscription -> ReferenceError TDZ. Era el
        // crash 'Cannot access _subscription before initialization' con el
        // servidor caido/504.
        let _subscription = null;
        _subscription = observable.subscribe(
        data => {
            if (_subscription){ _subscription.unsubscribe(); }
            if (data){
                if (error.status == 0){
                    console.error("[http] HTTP request received error 0");
                }
                observer.error(error);              // error in http request
            }
            else {
                observer.error({ status: 0 });       // offline
            }
        });
    }

    // Angular HttpClient IGNORA silenciosamente la opcion `timeout` (se cuela en
    // el objeto de opciones donde no existe). Sin timeout efectivo, una peticion
    // que se queda a medias en una reconexion medio-abierta (navigator.onLine==true
    // pero el servidor aun no responde) cuelga PARA SIEMPRE y bloquea el commit que
    // la UI espera ("se queda pensando y no da comanda"). El operador timeout() de
    // RxJS lo arregla de raiz: ademas de acotar la espera, al disparar se desuscribe
    // del source y HttpClient ABORTA el XHR subyacente (lo que sync.ts:1516/2958
    // lamentaba no poder hacer). El valor viaja en headers.timeout (get/post lo
    // ponen ahi); default 60s si no se especifica.
    private static readonly _CHECK_TIMEOUT_MS = 8000;
    private _angularTimeout(headers){
        let _ms = parseInt(headers && headers.timeout);
        return (isNaN(_ms) || _ms <= 0) ? 60000 : _ms;
    }

    // Un timeout disparado en una reconexion inestable es equivalente a offline:
    // lo normalizamos a {status:0} para que el motor de sync re-encole el cambio
    // en silencio (sin toast de "datos perdidos") en vez de contarlo como error
    // duro de servidor. Cualquier otro error se propaga tal cual.
    private _normalizeTimeout(error){
        return (error instanceof TimeoutError) ? { status: 0 } : error;
    }

    /********************************************/
    /* Browser -- use angular HttpClient        */
    /********************************************/

    private __angular_get(url, headers){
        let observable = new Observable((observer) => {
            try {
                let _subscription = this.angular.get(url, headers).pipe(timeout(this._angularTimeout(headers))).subscribe(
                    data => {
                        observer.next(data);
                    },
                    error => {
                        this._HttpError(observer, this._normalizeTimeout(error));
                    },
                    () => {
                        _subscription.unsubscribe();
                        observer.complete();
                    });
            }
            catch {
                observer.complete();
            }
        });

        return observable;
    }

    private __angular_post(url, data, headers){
        let observable = new Observable((observer) => {
            let _subscription = this.angular.post(url, data, headers).pipe(timeout(this._angularTimeout(headers))).subscribe(
            data => {
                observer.next(data);
            },
            error => {
                this._HttpError(observer, this._normalizeTimeout(error));
            },
            () => {
                _subscription.unsubscribe();
                observer.complete();
            });
        });

        return observable;
    }

    private __angular_check(url, headers){
        // Sonda de conectividad (base/online.php): timeout corto y fijo. Sin el,
        // el propio checkOnline colgaria en una reconexion medio-abierta y nunca
        // marcaria isOnline=false, dejando la deteccion offline muerta.
        return this.angular.get(url, headers).pipe(timeout(httpService._CHECK_TIMEOUT_MS));
    }
    
    private __angular_head(url, headers) {
        let observable = new Observable(observer => {
            let _subscription = this.angular.head(url, { headers: headers['headers'], withCredentials: headers['withCredentials'], observe: 'response' }).pipe(timeout(this._angularTimeout(headers))).subscribe(
            response => {
                if (response.status == 200) {
                    let _expectedContent = headers['headers'].get('Content-Type');
                    let _responseContent = response.headers.get('Content-Type');

                    observer.next(_expectedContent == _responseContent);
                    observer.complete();
                } else {
                    observer.next(false);
                    observer.complete();
                }
            },
            error => {
                this._HttpError(observer, this._normalizeTimeout(error));
            },
            () => {
                _subscription.unsubscribe();
                observer.complete();
            });
        });

        return observable;
    }

    /********************************************/
    /* Android / IOS -- use ionic HTTP          */
    /********************************************/
            
    private __cordova_get(url, headers, timeout, responseType){
        this.debuglog("[cordova] get url: " + url); 
        let observable = new Observable((observer) => {
            this.ionic.sendRequest(encodeURI(url), {
                method: 'get',
                headers: headers,
                timeout: (timeout === null) ? 60000 : timeout,
                responseType: responseType ? responseType : 'json'
            })
            .then(response => {
                this.debuglog("[cordova] reply to url: " + url, response);
                if (response.status == 200){
                    observer.next(response.data);
                    observer.complete();
                }
                else {
                    this._HttpError(observer, response);
                    observer.complete();
                }
            })
            .catch(response => {
                this.debuglog("[cordova] exteption", response);
                this._HttpError(observer, response);
                observer.complete();
            });
        });
        return observable;
    }
        
    private __cordova_post(url, data, headers, timeout, responseType, serializer){
        this.debuglog("[cordova] post url: " + url, data); 
        let observable = new Observable((observer) => {
            this.ionic.setDataSerializer(serializer);
            this.ionic.sendRequest(encodeURI(url), {
                method: 'post',
                data: data,
                headers: headers,
                timeout: (timeout === null) ? 60000 : timeout,
                responseType: responseType ? responseType : 'json'
            })
            .then(response => {
                this.debuglog("[cordova] reply to url: " + url, response);
                if (response.status == 200){
                    observer.next(response.data);
                    observer.complete();
                }
                else {
                    this._HttpError(observer, response);
                    observer.complete();
                }
            })
            .catch(response => {
                this.debuglog("[cordova] exteption", response);
                this._HttpError(observer, response);
                
                console.error(response); 
                observer.complete();
            });
        });
        return observable;
    }
    
    private __cordova_check(url, headers){
        this.debuglog("[cordova] test url: " + url); 
        let observable = new Observable((observer) => {
            this.ionic.sendRequest(encodeURI(url), {
                method: 'get',
                headers: headers,
                timeout: 5
            })
            .then(response => {
                this.debuglog("[cordova] reply to url: " + url, response);
                if (response.status == 200){
                    observer.next(response.data);
                    observer.complete();
                }
                else {
                    observer.error(response);
                    observer.complete();
                }
            })
            .catch(response => {
                this.debuglog("[cordova] exteption", response);
                this._HttpError(observer, response);
                
                console.error(response); 
                observer.complete();
            });
        });
        return observable;
    }

    private __cordova_head(url, headers){
        this.debuglog("[cordova] head url: " + url); 
        let observable = new Observable((observer) => {
            this.ionic.sendRequest(encodeURI(url), {
                method: 'head',
                headers: headers,
                timeout: 5
            })
            .then(response => {
                this.debuglog("[cordova] reply to url: " + url, response);
                if (response.status == 200){
                    let _expectedContent = headers['Content-Type'];
                    let _responseContent = response.headers['Content-Type'];

                    observer.next(_expectedContent == _responseContent);
                    observer.complete();                    
                }
                else {
                    observer.next(false);
                    observer.complete();
                }
            })
            .catch(response => {
                this.debuglog("[cordova] exteption", response);
                this._HttpError(observer, response);
                
                console.error(response); 
                observer.complete();
            });
        });
        return observable;
    }

    /********************************************/
    /* Public methods (platform dependant)      */
    /********************************************/
    
    get offlineObservable(){
        let observable = new Observable((observer) => {
            setTimeout(() => {
                observer.error({ status: 0 });
                observer.complete();
            }, 0);
        });
        return observable;
    }
    
    get(url, withCredentials = true, headers = httpService.headers, responseType = null, timeout = null){
        if (this.online.isOnline){
            if (this.platform.is('cordova')){
                return this.__cordova_get(url, headers, timeout, responseType);
            }

            let __angular_headers = {
                withCredentials: withCredentials,
                headers: new HttpHeaders(headers),
                timeout: ((timeout === null) ? 60000 : timeout).toString(),
                responseType: responseType ? responseType : 'json'
            };

            return this.__angular_get(url, __angular_headers);
        }
            
        return this.offlineObservable;   // offline
    }
        
    post(url, data, headers = httpService.headers, responseType = null, serializer = null, timeout = null){
        if (!serializer){
            serializer = httpService.serializer.json;
        }

        if (this.online.isOnline){
            if (this.platform.is('cordova')){
                return this.__cordova_post(url, data, headers, timeout, responseType, serializer);
            }

            let __angular_headers = {
                withCredentials: true,
                headers: new HttpHeaders((serializer == httpService.serializer.multipart) ? {} : headers),
                timeout: ((timeout === null) ? 60000 : timeout).toString(),
                responseType: responseType ? responseType : 'json'
            };

            return this.__angular_post(url, data, __angular_headers);
        }

        return this.offlineObservable;   // offline
    }
    
    check(){
        let url = AppConstants.baseURL + 'base/online.php';
        
        if (this.platform.is('cordova')){
            return this.__cordova_check(url, httpService.headers);
        }

        let __angular_headers = {
            withCredentials: true,
            headers: new HttpHeaders(httpService.headers)
        };
        
        return this.__angular_check(url, __angular_headers);
    }

    head(url, headers = httpService.headers){
        if (this.platform.is('cordova')){
            return this.__cordova_head(url, headers);
        }

        let __angular_headers = {
            withCredentials: true,
            headers: new HttpHeaders(headers)
        };
        
        return this.__angular_head(url, __angular_headers);
    }
}

/********************************************/
/* Available serializers                    */
/********************************************/

export namespace httpService {
    export enum serializer {
        json = 'json',
        text = 'text',
        utf8 = 'utf8',
        urlencoded = 'urlencoded',
        multipart = 'multipart',
        raw = 'raw'
    };
}
